import { IValueInputAttachable } from "client/Forms/attachable";
import {
	ReactElement,
	ReadonlyRefObject,
	Ref,
	useCallback,
	useImperativeHandle,
	useRef,
	useState
} from "react";
import {
	CommunityPollAnswerFormData,
	CommunityPollFormData,
	MAX_POLL_ANSWERS_COUNT,
	MIN_POLL_ANSWERS_COUNT
} from "./common";
import {
	generateTrackingId,
	isNil,
	isNilOrWhiteSpace,
	isNotNil,
	setTrackingId
} from "client/Misc/utils";
import { CommunityPostPollViewModel } from "client/Api/Communities/communityPostViewer";
import { localize } from "client_legacy/ndaw/culture";
import {
	AutogrowTextArea,
	FormItem,
	IconButton,
	IconButtonWithTitle,
	ReorderableList,
	TextInput,
	Toggle,
	useConfirmationDialog,
	ValidationError
} from "client/Components";
import { getClientId, setClientId } from "client/Misc/clientId";
import { InputValidationResult, InputValidationResultKind } from "client/Validation";
import styles from "./PollEditor.module.less";
import classNames from "classnames";
import { Guid } from "client_legacy/ndaw/guid";
import { getTotalVotes } from "../PollViewer/PollViewer";

export type PollEditorProps = {
	initialPoll?: CommunityPostPollViewModel | null;
	attachable: IValueInputAttachable<OptionalModel<CommunityPollFormData | null>>;
	onFocus: () => void;
};

export const PollEditor = (props: PollEditorProps): ReactElement => {
	const {
		attachable,
		initialPoll,
		onFocus
	} = props;

	const [confirmationDialog, openConfirmationDialog] = useConfirmationDialog();
	const attachableValue: OptionalModel<CommunityPollFormData | null> = attachable.value;
	const isReadOnly: boolean = !attachableValue.isSet && (isNotNil(initialPoll));

	const deleteHandler = useCallback(
		async (): Promise<void> => {
			if (isReadOnly) {
				let hasAnyVotes: boolean = false;

				if (initialPoll) {
					const {
						statistics
					} = initialPoll;

					hasAnyVotes = getTotalVotes(statistics.voteCounts) > 0;
				}

				const result: TryResult<void> = await openConfirmationDialog({
					title: localize("_AreYouSureYouWantToRemoveThisPoll"),
					confirmText: localize("_Delete"),
					trackingId: "delete-poll-confirmation-dialog",
					children: hasAnyVotes
						? localize("_ThereAreAlreadyAnswersToThisPoll")
						: undefined
				});

				if (!result.wasSuccessful) {
					return;
				}
			}

			attachable
				.reducer
				.setValue({
					isSet: true,
					value: null
				});

			attachable
				.validation
				.triggerInputFinished();
		},
		[
			attachable,
			isReadOnly,
			initialPoll,
			openConfirmationDialog
		]
	);

	const trackingId: string = attachable
		.attachToTextInput()
		.trackingId;

	return (
		<>
			<div {...setTrackingId(trackingId)}>
				<div className={styles.header}>
					<IconButtonWithTitle
						icon="xmark"
						onClick={deleteHandler}
						title={localize("_RemovePoll")}
						trackingId="remove-poll"
					/>
				</div>

				{!attachableValue.isSet && (isNotNil(initialPoll)) && (
					<ReadOnlyPoll
						poll={initialPoll}
					/>
				)}

				{attachable.whenType(
					x => isSetModelWithValue(x),
					handler => (
						<EditablePoll
							attachable={handler.for("value")}
							onFocus={onFocus}
						/>
					)
				)}
			</div>
			{confirmationDialog}
		</>
	);
};
PollEditor.displayName = "PollEditor";

type ReadOnlyPollProps = {
	poll: CommunityPostPollViewModel;
};

const ReadOnlyPoll = (props: ReadOnlyPollProps): ReactElement => {
	const {
		poll
	} = props;

	return (
		<>
			<FormItem>
				<div className={styles.question}>
					<AutogrowTextArea
						value={poll.question}
						onChange={() => {/** NOPE */}}
						isDisabled
						hasNoMinHeight
						hasNoPadding
						isBorderless
						trackingId={QUESTION_TRACKING_ID}
					/>
				</div>
			</FormItem>
			<FormItem>
				<div {...setTrackingId(ANSWERS_LIST_TRACKING_ID)}>
					{poll
						.options
						.map(
							(x, index): ReactElement => {
								const isLastItem: boolean = index === poll
									.options
									.length - 1;

								return (
									<ReorderableList.RowItemLike
										key={x.id}
										trackingId={generateTrackingId("answer-item", `|${x.id}`)}
										hasSeparator
										isLastItem={isLastItem}
									>
										<div
											className={classNames(
												styles.answerText,
												styles.answerText_disabled
											)}
										>
											{x.text}
										</div>
									</ReorderableList.RowItemLike>
								);
							}
						)
					}
				</div>
			</FormItem>
			<FormItem>
				<Toggle
					value={poll.isMultiSelect}
					onChange={() => { /** NOPE */ }}
					isDisabled
					text={localize("_AllowMultipleAnswers")}
					trackingId={MULTI_SELECT_TRACKING_ID}
				/>
			</FormItem>
		</>
	);
};
ReadOnlyPoll.displayName = "ReadOnlyPoll";

const QUESTION_TRACKING_ID: string = "question";
const MULTI_SELECT_TRACKING_ID: string = "multi-select";
const ANSWERS_LIST_TRACKING_ID: string = "answers-list";

type EditablePollProps = {
	attachable: IValueInputAttachable<CommunityPollFormData>;
	onFocus: () => void;
};

const EditablePoll = (props: EditablePollProps): ReactElement => {
	const {
		attachable,
		onFocus
	} = props;

	const attachableValue: CommunityPollFormData = attachable.value;
	const answersAttachable: IValueInputAttachable<readonly CommunityPollAnswerFormData[]> = attachable.for("answers");
	const itemRefMap: ReadonlyRefObject<Map<Guid, ItemHandle>> = useRef<Map<Guid, ItemHandle>>(new Map());

	const setItemRefHandler = useCallback(
		(clientId: Guid, handle: ItemHandle | null): void => {
			const { current: map } = itemRefMap;

			if (handle) {
				map.set(clientId, handle);
			} else {
				map.delete(clientId);
			}
		},
		[]
	);

	const addHandler = useCallback(
		(): void => {
			const item: CommunityPollAnswerFormData = setClientId({ text: "" });

			answersAttachable
				.reducer
				.push(item);

			answersAttachable
				.validation
				.triggerInputFinished();

			const clientId: Guid = getClientId(item);

			// Wait for the item to be rendered and the ref to be available
			setTimeout(
				(): void => {
					itemRefMap.current
						.get(clientId)
						?.edit();
				},
				1
			);
		},
		[answersAttachable]
	);

	const itemMoveHandler = useCallback(
		(fromIndex: number, toIndex: number): void => {
			const itemsCopy: CommunityPollAnswerFormData[] = [...attachableValue.answers];
			const item: CommunityPollAnswerFormData = itemsCopy.splice(fromIndex, 1)[0];
			itemsCopy.splice(toIndex, 0, item);

			answersAttachable
				.reducer
				.setValue(itemsCopy);

			answersAttachable
				.validation
				.triggerInputFinished();
		},
		[attachableValue, answersAttachable]
	);

	const deleteHandler = useCallback(
		(index: number): void => {
			answersAttachable
				.reducer
				.removeAt(index);

			answersAttachable
				.validation
				.triggerInputFinished();
		},
		[answersAttachable]
	);

	const answersCount: number = attachableValue
		.answers
		.length;

	return (
		<>
			<FormItem>
				<div className={styles.question}>
					<AutogrowTextArea
						{...attachable.for("question").attachToTextArea()}
						hasNoMinHeight
						hasNoPadding
						isBorderless
						autoFocus
						placeholder={localize("_WhatIsYourQuestion")}
						onFocus={() => onFocus()}
						trackingId={QUESTION_TRACKING_ID}
					/>
				</div>
				<ValidationError {...attachable.for("question").attachToValidationError()}/>
			</FormItem>
			<FormItem>
				<ReorderableList
					onItemMove={itemMoveHandler}
					hasSeparator
					trackingId={ANSWERS_LIST_TRACKING_ID}
				>
					{attachable.for("answers")
						.map(
							(x, index): ReactElement => {
								const clientId: Guid = getClientId(x.value);

								return (
									<Item
										key={clientId}
										ref={(ref) => setItemRefHandler(clientId, ref)}
										attachable={x}
										onDelete={
											answersCount > MIN_POLL_ANSWERS_COUNT
												? () => deleteHandler(index)
												: undefined
										}
										onFocus={onFocus}
									/>
								);
							}
						)
					}
				</ReorderableList>
				{answersCount < MAX_POLL_ANSWERS_COUNT && (
					<ReorderableList.AddButton
						trackingId="add-answer"
						onClick={addHandler}
					/>
				)}
				<ValidationError {...answersAttachable.attachToValidationError(true)} />
			</FormItem>
			<FormItem>
				<Toggle
					{...attachable.for("isMultiSelect").attachToCheckbox()}
					text={localize("_AllowMultipleAnswers")}
					trackingId={MULTI_SELECT_TRACKING_ID}
				/>
				<ValidationError {...attachable.for("isMultiSelect").attachToValidationError()}/>
			</FormItem>
		</>
	);
};

type ItemHandle = {
	edit: () => void;
};

type ItemProps = {
	attachable: IValueInputAttachable<CommunityPollAnswerFormData>;
	ref?: Ref<ItemHandle>;
	onDelete?: () => void;
	onFocus: () => void;
};

const Item = (props: ItemProps): ReactElement => {
	const {
		ref,
		attachable,
		onDelete,
		onFocus
	} = props;

	const [isInEditMode, setIsInEditMode] = useState<boolean>(false);

	const attachableValue: CommunityPollAnswerFormData = attachable.value;
	const textValidationState: InputValidationResult = attachable
		.for("text")
		.validation
		.getValidationState();

	const errormessage: string | undefined = textValidationState.kind === InputValidationResultKind.Invalid
		? textValidationState.errorMessage
		: undefined;

	useImperativeHandle(
		ref,
		(): ItemHandle => ({
			edit: () => setIsInEditMode(true)
		}),
		[]
	);

	return (
		<ReorderableList.RowItem
			trackingId={generateTrackingId("answer-item", `|${getClientId(attachableValue)}`)}
			mouseInteraction={{
				draggableMode: isInEditMode
					? "handle"
					: "entire-row"
			}}
			validationMessage={errormessage}
			actions={(
				<IconButton
					trackingId="delete"
					icon="trash-can"
					kind="transparent"
					isDisabled={isNil(onDelete)}
					onClick={onDelete}
					shape="round"
				/>
			)}
		>
			<div className={styles.answerContent}>
				{isInEditMode
					? (
						<TextInput
							{...attachable.for("text").attachToTextInput()
								.withOnBlur(() => setIsInEditMode(false))
							}
							placeholder={localize("_EnterAnswer")}
							autoFocus="focus"
							onFocus={onFocus}
						/>
					)
					: (
						<div
							{...setTrackingId("text")}
							className={styles.answerText}
							onClick={() => setIsInEditMode(true)}
						>
							{isNilOrWhiteSpace(attachableValue.text)
								? (
									<span className={styles.placeholder}>
										{localize("_EnterAnswer")}
									</span>
								)
								: attachableValue.text
							}
						</div>
					)
				}
			</div>
		</ReorderableList.RowItem>
	);
};
Item.displayName = "Item";

function isSetModelWithValue(model: OptionalModel<CommunityPollFormData | null>): model is SetOptionalModel<CommunityPollFormData> {
	return model.isSet && isNotNil(model.value);
}